import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, ElementRef, Inject, NgZone, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';

import { TranslateService } from '@ngx-translate/core';

import { DomController, IonContent, ViewDidEnter, ViewWillEnter, ViewWillLeave } from '@ionic/angular';

import { SplashScreenPlugin } from '../../core/services/native-plugins/splash-screen.plugin';
import { StatusBarPlugin } from '../../core/services/native-plugins/status-bar.plugin';

import { forkJoin, from, Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, filter, map, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';

import { AreaSelectionModalPage, AreaSelectionModalPageIdentifier } from '../../modals/area-selection-modal/area-selection-modal.page';
import {
  ItemAvailabilityDateTimePickerModalPage,
  ItemAvailabilityDateTimePickerModalPageIdentifier,
  ItemAvailabilityDateTimePickerModalPageParams,
  ItemAvailabilityDateTimePickerModalPageResult,
} from '../../modals/item-availability-date-time-picker-modal/item-availability-date-time-picker-modal.page';
import { PreviousOrderModalPage, PreviousOrderModalPageIdentifier } from '../../modals/previous-order-modal/previous-order-modal.page';
import {
  VendorChatExplainerModalIdentifier,
  VendorChatExplainerModalPage,
} from '../../modals/vendor-chat-explainer-modal/vendor-chat-explainer-modal.page';
import { VendorReviewsModalPage, VendorReviewsModalPageIdentifier } from '../../modals/vendor-reviews-modal/vendor-reviews-modal.page';

import { AnnouncementType } from '../../core/enums/announcement-type.enum';
import { Availability } from '../../core/enums/availability.enum';
import { OrderMenuItemSourceType } from '../../core/enums/order-menu-item-source-type.enum';

import { Address } from '../../core/models/address.model';
import { Badge } from '../../core/models/badge.model';
import { CatererRating } from '../../core/models/caterer-rating.model';
import { CustomScrollEvent, ScrollEventDetail } from '../../core/models/custom-scroll-event.model';
import { Eta } from '../../core/models/eta.model';
import { GroceryItemsWithCategories } from '../../core/models/grocery-category-info.model';
import { GroceryVendorDetails } from '../../core/models/grocery-vendor-details.model';
import { MultiLanguageValue } from '../../core/models/multi-language-value.model';
import { CatererReview } from '../../core/models/review.model';
import { VendorDetails, VendorMenuItem, VendorPreviousOrderDetails } from '../../core/models/vendor-details.model';

import { AccountService } from '../../core/services/account.service';
import { AddressService } from '../../core/services/address.service';
import { AnalyticsService } from '../../core/services/analytics.service';
import { AppExplainersService } from '../../core/services/app-explainers.service';
import { AreasService } from '../../core/services/areas.service';
import { BackgroundService } from '../../core/services/background.service';
import { CartService } from '../../core/services/cart.service';
import { ContactService } from '../../core/services/contact.service';
import { DateTimeService } from '../../core/services/date-time.service';
import { DeepLinkService } from '../../core/services/deep-link.service';
import { LazyLoadingImagesService } from '../../core/services/lazy-loading-images.service';
import { LoggerService } from '../../core/services/logger.service';
import { ModalService } from '../../core/services/modal.service';
import { NavigationService } from '../../core/services/navigation.service';
import { NoticePeriodService } from '../../core/services/notice-period.service';
import { OverlayService } from '../../core/services/overlay.service';
import { ReferralProgramService } from '../../core/services/referral-program.service';
import { SafeAreaInsetsService } from '../../core/services/safe-area-insets.service';
import { ScrollService } from '../../core/services/scroll.service';
import { SettingsService } from '../../core/services/settings.service';
import { PlatformService } from '../../core/services/ssr/platform.service';
import { WindowService } from '../../core/services/ssr/window.service';
import { UniversalLinksService } from '../../core/services/universal-links.service';
import { ValidationService } from '../../core/services/validation.service';
import {
  LocalMenuGroup,
  MAX_TIME_TO_REFRESH_RESULTS_FOR_NOW_IN_MINUTES,
  MIN_TIME_TO_REFRESH_RESULTS_FOR_LATER_IN_MINUTES,
  MIN_TIME_TO_REFRESH_RESULTS_FOR_NOW_IN_MINUTES,
  VendorsService,
} from '../../core/services/vendors.service';

import { AnalyticsConfig, TOKEN_ANALYTICS_CONFIG } from '../../core/config/analytics.config';

import {
  GroceryItemDetailsModalComponent,
  GroceryItemDetailsModalPageIdentifier,
  GroceryItemDetailsModalParams,
} from '../../modals/grocery-item-details-modal/grocery-item-details-modal.component';
import {
  MenuItemDetailsModalComponent,
  MenuItemDetailsModalPageIdentifier,
  MenuItemDetailsModalParams,
  MenuItemDetailsModalResult,
} from '../../modals/menu-item-details-modal/menu-item-details-modal.component';

import { ErrorPageParams } from '../error/error-routing.module';

import { Helpers } from '../../core/helpers';

import { VendorDetailsPageAnimations } from './vendor-details.animations';

import { SwiperOptions } from 'swiper';
import { toPromise } from '../../core/operators/to-promise';

const SPACE_BETWEEN_HEADER_AND_CONTENT = 90;
const HEADER_IMAGE_OVERFLOW = 20;
const HEADER_IMAGE_HEIGHT_TRANSITION_IN_MS = 200;
const HEADER_IMAGE_OPACITY_TRANSITION_IN_MS = 300;

const FINISH_LOADING_PAGE_DELAY_IN_MS = 500;
const SCROLL_TO_GROUP_DURATION_IN_MS = 300;

// 54 (tabs) + 8 (margin top) + 12 (margin bottom)
// Note: We can't get the height from the DOM element because
// the height of the tabbar is set to 0 when is not shown
const TABBAR_HEIGHT = 74;

// 68 (message) + 7 (margin-bottom)
const FILTERED_ITEMS_SECTION_HEIGHT = 75;

interface TabDetail {
  serviceTypeName?: string;
  serviceType?: number;
  name: string;
}

interface GroupSideMenuDetail {
  serviceTypeName: string;
  groups: Array<{
    name: string;
    tabIndex: number;
    itemsCount?: number;
  }>;
}

@Component({
  selector: 'app-vendor-details',
  templateUrl: './vendor-details.page.html',
  styleUrls: ['./vendor-details.page.scss'],
  animations: [...VendorDetailsPageAnimations],
  encapsulation: ViewEncapsulation.None,
})
export class VendorDetailsPage implements OnInit, AfterViewInit, ViewWillEnter, ViewDidEnter, ViewWillLeave, OnDestroy {
  @ViewChild(IonContent)
  public content: IonContent;

  @ViewChild('browserHeader', { read: ElementRef })
  public browserHeader: ElementRef<HTMLElement>;

  @ViewChild('cordovaHeader', { read: ElementRef })
  public cordovaHeader: ElementRef<HTMLElement>;

  @ViewChild('headerImage', { read: ElementRef })
  public headerImage: ElementRef<HTMLElement>;

  @ViewChild('footer', { read: ElementRef })
  public footer: ElementRef<HTMLElement>;

  @ViewChild('vendorName', { read: ElementRef })
  public vendorName: ElementRef<HTMLElement>;

  @ViewChild('groupsTabs', { read: ElementRef })
  public groupsTabsElement: ElementRef<HTMLElement>;

  @ViewChild('groupsTabsSentinel', { read: ElementRef })
  public groupsTabsSentinelElement: ElementRef<HTMLElement>;

  @ViewChild('vendorMenuGroups', { read: ElementRef })
  public vendorMenuGroupsElements: ElementRef<HTMLElement>;

  public vendorDetails: VendorDetails;
  public groceryVendorDetails: GroceryVendorDetails;

  public vendorId: number;
  public name: string;
  public description: string;
  public names: MultiLanguageValue;
  public imageUrl: string;
  public eta?: Eta;
  public policy?: string;
  public rating?: CatererRating;
  public badges?: Array<Badge>;
  public minEta: { title: string; description: string };
  public deliveryInfo: Array<{ serviceType: number; serviceTypeName: string; minOrderValue: number; minimumDeliveryCharge: number }>;

  public alternativeServiceTypeAvailableDetails: { message: string; serviceType: number };

  public minimumDeliveryCharge?: number;
  public sameDeliveryAcrossServiceTypes = false;
  public minOrderValue?: number;
  public sameMinOrderValueAcrossServiceTypes = false;

  public Availability: typeof Availability = Availability;

  public isIos: boolean;
  public isBrowser: boolean;
  public isCordovaTablet: boolean;
  public selectedAddress: Address;

  public cartItemsCount = 0;
  public amountRemainingToMinimumOrderValue = 0;
  public selectedServiceTypes: number;
  public isSelectedServiceTypeBasedOnCategories: boolean;
  public isSelectedServiceTypeBasedOnDateTime: boolean;
  public isQuickAddToCartSupported: boolean;
  public isFilteringItems: boolean;
  public selectedTagsNames: string;

  // When reloading the vendor details because of a change in the date/time we
  // need to hide (visibility) the content, scroll to the top of the page and then
  // remove the content (display) from the DOM so we need two properties instead of one
  public showVendorDetails = false;
  public hideVendorDetails = false;

  public addToCartBufferTimeInMinutes: number;

  public tabs: Array<TabDetail> = [];
  public groupsSideMenuDetailsCount = 0;
  public groupsSideMenuDetails: Array<GroupSideMenuDetail> = [];
  public activeTabIndex: number;
  public categoryDetails: GroceryItemsWithCategories;
  public previousOrders: Array<VendorPreviousOrderDetails> = [];
  public allPreviousOrdersHaveOnlyOneItem: boolean;
  public openingTime: string;
  public closingTime: string;
  public chatPhoneNumber?: string;

  public showChatButtonExpanded = true;
  public isAnimatingChatButton = false;

  public isTabBarFixedAtTop = false;
  public cordovaHeaderHeight: number;
  public showCordovaHeaderBackground = false;
  public showNavbarPaddingTop: boolean;
  public isETAOpen = false;
  public isVendorDeliveryInfoOpen = false;
  public isVendorMinOrderInfoOpen = false;
  public isVendorPolicyOpen = false;
  public isVendorGroupsOpen = false;
  public tabbarHeight = TABBAR_HEIGHT;
  public filteredItemsSectionHeight = FILTERED_ITEMS_SECTION_HEIGHT;
  public contentMarginTop = 0;
  public headerImageMinHeight = 0;

  // Because of the stretchy header we're setting the background
  // to be grey so we need to make sure the content covers the
  // entire page so that the background is not visible
  public contentMinHeight: number = undefined;

  // We use this property to not allow the user to leave
  // the page while it is still loading because that causes
  // a very ugly issue in Angular
  // https://github.com/angular/angular/issues/19712
  public finishedLoadingPage = false;

  public sliderScrollingDirection: string;
  public sliderConfig: SwiperOptions = {
    slidesPerView: 'auto',
    spaceBetween: 12,
    freeMode: true,
  };

  public selectedAreaId: number;
  public selectedDateTime: string;
  public isOrderForNowSelected: boolean;

  private currentY: number;
  private currentTouchEventsCount = 0;
  private isUpdatingHeaderImageAutomatically = false;

  private iniatializedHeaderImage = false;
  private scrollElement: HTMLElement;
  private headerImageElement: HTMLElement;
  private errorWhenSettingHeaderImage = false;

  private headerImageInitialWidth: number;
  private headerImageInitialHeight: number;
  private headerImageCurrentHeight: number;
  private headerImageIntrinsicWidth: number;
  private headerImageIntrinsicHeight: number;

  private reviews: Array<CatererReview> = [];

  private groupsElements: Array<HTMLElement>;
  private shouldUpdateActiveGroupOnScroll = true;
  private stickyTabsObserver: IntersectionObserver;
  private activeGroupObserver: IntersectionObserver;

  private menuItemId?: number;
  private initialMenuItemTagsIds?: Array<number>;

  private vendorServiceTypes: number;
  private sourceId?: number;
  private sourceType?: OrderMenuItemSourceType;
  private lazyLoadingImagesObserversIds: Array<string> = [];

  private updateActiveTab$ = new Subject<number>();
  private unsubscribe$ = new Subject<void>();

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private elementRef: ElementRef<HTMLElement>,
    private ngZone: NgZone,
    private title: Title,
    private domCtrl: DomController,
    private modalService: ModalService,
    private contactService: ContactService,
    private areasService: AreasService,
    private accountService: AccountService,
    private addressService: AddressService,
    private vendorsService: VendorsService,
    private deepLinkService: DeepLinkService,
    private navigationService: NavigationService,
    private overlayService: OverlayService,
    private validationService: ValidationService,
    private scrollService: ScrollService,
    private cartService: CartService,
    private settingsService: SettingsService,
    private windowService: WindowService,
    private platformService: PlatformService,
    private loggerService: LoggerService,
    private dateTimeService: DateTimeService,
    private backgroundService: BackgroundService,
    private analyticsService: AnalyticsService,
    private noticePeriodService: NoticePeriodService,
    private referralProgramService: ReferralProgramService,
    private universalLinksService: UniversalLinksService,
    private translateService: TranslateService,
    private lazyLoadingImagesService: LazyLoadingImagesService,
    private statusBarPlugin: StatusBarPlugin,
    private safeAreaInsetsService: SafeAreaInsetsService,
    private splashScreenPlugin: SplashScreenPlugin,
    private appExplainersService: AppExplainersService,
    @Inject(TOKEN_ANALYTICS_CONFIG) private analyticsConfig: AnalyticsConfig,
  ) {}

  ngOnInit() {
    // This page could be opened using the vendor slug so we use the getVendorIdResolver
    // to make sure we always have the vendor ID — DO NOT get the vendor ID from the URL!
    this.vendorId = this.activatedRoute.snapshot.data.vendorId as number;

    this.menuItemId = this.navigationService.getItemIdFromUrl(this.activatedRoute.snapshot);
    this.initialMenuItemTagsIds = this.navigationService.getTagsIdsFromUrl(this.activatedRoute.snapshot);
    this.selectedAreaId = this.navigationService.getAreaIdFromUrl(this.activatedRoute.snapshot);

    this.selectedServiceTypes = this.validateVendorServiceTypes(
      this.navigationService.getServiceTypeFromUrl(this.activatedRoute.snapshot),
      this.vendorsService.getVendorSlugAndServiceTypeById(this.vendorId).vendorServiceTypes,
    );

    const { dateTime, orderForNow } = this.navigationService.getDateTimeAndOrderForNowFromUrl(this.activatedRoute.snapshot);
    this.selectedDateTime = dateTime ?? undefined;
    this.isOrderForNowSelected = orderForNow ?? false;

    const { sourceId, sourceType } = this.navigationService.getSourceIdAndTypeFromUrl(this.activatedRoute.snapshot);
    this.sourceId = sourceId;
    this.sourceType = sourceType;

    this.isIos = this.platformService.isIos;
    this.isBrowser = this.platformService.isBrowser;
    this.isCordovaTablet = this.platformService.isCordovaTablet;
    this.showNavbarPaddingTop = !this.safeAreaInsetsService.getSafeAreaInsetTop();
    this.isQuickAddToCartSupported = this.settingsService.isQuickAddToCartSupported(this.selectedServiceTypes);
    this.isSelectedServiceTypeBasedOnDateTime = this.settingsService.isServiceTypeBasedOnDateTime(this.selectedServiceTypes);
    this.isSelectedServiceTypeBasedOnCategories = this.settingsService.isServiceTypeBasedOnCategories(this.selectedServiceTypes);
    this.addToCartBufferTimeInMinutes = this.cartService.getAddToCartBufferTimeInMinutes();
    this.sliderScrollingDirection = this.settingsService.getLanguageDirection();

    this.activatedRoute.queryParamMap
      .pipe(
        startWith(this.activatedRoute.snapshot.queryParamMap),
        filter(() => this.finishedLoadingPage),
        debounceTime(100),
      )
      .subscribe((queryParamMap) => {
        this.handleUrlChanges(queryParamMap);
      });

    this.updateActiveTab$
      .pipe(
        // Update the selected group immediately but debounce
        // centering the selected tab just in case if the user
        // scrolls up/down several sections very quickly
        filter((tabIndex) => !this.activeTabIndex || this.activeTabIndex !== tabIndex),
        tap((tabIndex) => this.ngZone.run(() => (this.activeTabIndex = tabIndex))),
        debounceTime(50),
        tap((tabIndex) => void this.scrollHorizontallyToSelectedTab(tabIndex)),
        takeUntil(this.unsubscribe$),
      )
      .subscribe();

    this.cartService.cart$.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      const minimumOrderValue = this.cartService.getVendorMinimumOrderValue(this.vendorId);
      const vendorItemsTotalPrice = this.cartService.getVendorItemsTotalPrice(this.vendorId);

      this.cartItemsCount = this.cartService.getItemsCount();
      this.amountRemainingToMinimumOrderValue = minimumOrderValue
        ? Helpers.subtractWithPrecision(minimumOrderValue, vendorItemsTotalPrice)
        : 0;
    });

    if (this.isBrowser) {
      const chatId = this.navigationService.getChatIdFromUrl(this.activatedRoute.snapshot);
      if (chatId) {
        const chatRequestedByUserId = this.navigationService.getChatRequestedByUserIdFromUrl(this.activatedRoute.snapshot);
        const chatRequestedByUserEmail = this.navigationService.getChatRequestedByUserEmailFromUrl(this.activatedRoute.snapshot);

        void this.analyticsService.trackEvent({
          name: this.analyticsConfig.eventName.vendorDetailsPageChatLinkClicked,
          data: {
            [this.analyticsConfig.propertyName.catererId]: this.vendorId,
            [this.analyticsConfig.propertyName.chatId]: chatId,
            [this.analyticsConfig.propertyName.chatRequestedByUserId]: chatRequestedByUserId,
            [this.analyticsConfig.propertyName.chatRequestedByUserEmail]: chatRequestedByUserEmail,
          },
        });
      }
    }
  }

  ngAfterViewInit() {
    void this.initializeScrollElementAndCordovaElementsSizes();
  }

  ionViewWillEnter() {
    if (!this.iniatializedHeaderImage) {
      this.iniatializedHeaderImage = true;
      this.headerImageElement = this.headerImage?.nativeElement ?? null;
      this.initializeHeaderImagePlaceholder();
    }

    this.updateStatusBarColorBasedOnHeaderStatus();
  }

  ionViewDidEnter() {
    this.splashScreenPlugin.hide();
    this.trackPageView();

    const shouldInitializePage = !this.finishedLoadingPage;

    if (shouldInitializePage) {
      void this.loadVendorDetailsAndInitializePage().then(() => {
        this.trackVendorViewedCleverTapEvent();
      });
      return;
    }

    const canContinueAfterBackgroundCheck = this.maybeHandleAppBeingInBackgroundForTooLong();

    if (!canContinueAfterBackgroundCheck) {
      return;
    }
  }

  ionViewWillLeave(): void {
    void this.statusBarPlugin.useDarkTextColor();

    // The page can be closed automatically when getting a push notification that
    // redirects the user somewhere else so we need to make sure we hide any modal
    // before leaving this page
    void this.modalService.dismissTopModal();
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
    this.stickyTabsObserver?.disconnect();
    this.activeGroupObserver?.disconnect();
    this.scrollElement.removeEventListener('touchstart', this.handleTouchStartEvent);
    this.scrollElement.removeEventListener('touchmove', this.handleTouchMoveEvent);
    this.scrollElement.removeEventListener('touchend', this.handleTouchEndEvent);
    this.scrollElement.removeEventListener('touchcancel', this.handleTouchEndEvent);
    this.lazyLoadingImagesObserversIds.forEach((observerId) => this.lazyLoadingImagesService.disconnectObserver(observerId));
  }

  public onOpenPreviousOrderModal(previousOrderDetails: VendorPreviousOrderDetails): void {
    void this.modalService.showSheet({
      id: PreviousOrderModalPageIdentifier,
      component: PreviousOrderModalPage,
      breakpoints: [0, 1],
      initialBreakpoint: 1,
      componentProps: {
        selectedAddress: this.selectedAddress,
        selectedDateTime: this.selectedDateTime,
        isOrderForNowSelected: this.isOrderForNowSelected,
        ordersVendors: [{ vendorId: this.vendorId, orderId: previousOrderDetails.orderId }],
      },
    });
  }

  public onOpenSearch(): void {
    if (this.isSelectedServiceTypeBasedOnCategories) {
      this.deepLinkService.openVendorSearch(this.vendorId, {
        vendorDetails: this.groceryVendorDetails,
        selectedServiceType: this.selectedServiceTypes,
        selectedAddress: this.selectedAddress,
      });
    } else {
      this.deepLinkService.openVendorSearch(this.vendorId, {
        vendorDetails: this.vendorDetails,
        selectedServiceType: this.selectedServiceTypes,
        selectedAddress: this.selectedAddress,
        selectedDateTime: this.selectedDateTime,
        isOrderForNowSelected: this.isOrderForNowSelected,
        sourceId: this.sourceId,
        sourceType: this.sourceType,
      });
    }
  }

  public onChangeTab(newTabIndex: number): void {
    this.updateActiveTab$.next(newTabIndex);

    if (!this.groupsElements?.length || newTabIndex > this.groupsElements.length - 1) {
      return;
    }

    // Don't update the selected tab when scrolling since we are
    // scrolling the page and not the user
    this.shouldUpdateActiveGroupOnScroll = false;

    this.domCtrl.read(() => {
      const targetGroup = this.groupsElements[newTabIndex];
      const extraScrollDistance = 4;

      const targetScrollTop =
        targetGroup.getBoundingClientRect().top +
        extraScrollDistance +
        this.scrollElement.scrollTop -
        this.tabbarHeight -
        (this.isBrowser ? this.browserHeader?.nativeElement.getBoundingClientRect().height || 0 : this.cordovaHeaderHeight) -
        (this.isBrowser ? 0 : this.isFilteringItems ? this.filteredItemsSectionHeight || 0 : 0);

      if (this.scrollElement.scrollTop === targetScrollTop) {
        this.shouldUpdateActiveGroupOnScroll = true;
        return;
      }

      void this.content
        .scrollToPoint(0, targetScrollTop, SCROLL_TO_GROUP_DURATION_IN_MS)
        .then(() => (this.shouldUpdateActiveGroupOnScroll = true));
    });
  }

  public onSelectGroupSideMenuOption(newGroupIndex: number): void {
    this.isVendorGroupsOpen = false;
    this.onChangeTab(newGroupIndex);
  }

  public onOpenDifferentServiceTypeInNewTab(serviceTypes: number): void {
    if (!serviceTypes || !this.windowService.isWindowDefined) {
      return;
    }

    const urlTree = this.router.parseUrl(this.router.url);
    urlTree.fragment = serviceTypes.toString();

    this.windowService.window.open(urlTree.toString(), '_blank');
  }

  public onOpenDateTimePicker(): void {
    const maybeSelectAddress = this.selectedAddress
      ? Promise.resolve(this.selectedAddress)
      : this.modalService.showCard<Address>({
          component: AreaSelectionModalPage,
          id: AreaSelectionModalPageIdentifier,
          presentingElement: this.elementRef.nativeElement,
          componentProps: {
            showCloseButton: true,
            selectedAddress: this.selectedAddress,
          },
        });

    void maybeSelectAddress.then((selectedAddress) => {
      if (!selectedAddress) {
        return;
      }

      void this.overlayService
        .showAreaDateTimePicker({
          hideAddressPicker: true,
          enableBackdropDismiss: true,
          isOrderForNowAvailable: this.settingsService.isOrderForNowAvailableInServiceType(this.selectedServiceTypes),
          selectedAddress,
          selectedDateTime: this.selectedDateTime,
          isOrderForNowSelected: this.isOrderForNowSelected,
          availableDateTimesAsync: this.vendorsService.getVendorAvailableDateTimes({
            areaId: selectedAddress.areaId,
            vendorId: this.vendorId,
            itemsIds: [],
          }),
        })
        .then((areaDateTimeResults) => {
          if (areaDateTimeResults) {
            areaDateTimeResults = {
              ...areaDateTimeResults,
              selectedAddress,
              hasChangedData:
                areaDateTimeResults.hasChangedData || this.addressService.hasLocationChanged(selectedAddress, this.selectedAddress),
            };
          }

          if (areaDateTimeResults && areaDateTimeResults.hasChangedData) {
            const address = areaDateTimeResults.selectedAddress;
            const dateTime = areaDateTimeResults.selectedDateTime;
            const orderForNow = areaDateTimeResults.isOrderForNowSelected;

            this.navigationService.updateServiceTypeAreaDateTimeFromUrl(
              this.activatedRoute,
              this.selectedServiceTypes,
              address.area.areaId,
              dateTime,
              orderForNow,
            );
          }
        });
    });
  }

  public onOpenReviews(): void {
    this.trackOpenReviews();

    const openReviewsModal = () =>
      void this.overlayService.hideLoading().then(() =>
        this.modalService.showCard({
          component: VendorReviewsModalPage,
          id: VendorReviewsModalPageIdentifier,
          presentingElement: this.elementRef.nativeElement,
          componentProps: { vendorId: this.vendorId, rating: this.rating, reviews: this.reviews },
        }),
      );

    if (this.reviews?.length) {
      openReviewsModal();
      return;
    }

    void this.overlayService.showLoading().then(() => {
      this.vendorsService
        .getReviews(this.vendorId)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe({
          next: (reviews) => {
            this.reviews = reviews;
            openReviewsModal();
          },
          error: (error: HttpErrorResponse) => {
            const errorMessage = this.validationService.getErrorMessage(error);
            this.loggerService.info({
              component: 'VendorDetailsPage',
              message: "couldn't get vendor reviews",
              details: { error, messageShownToUser: errorMessage },
            });

            const pageParams: ErrorPageParams = { message: errorMessage };
            void this.overlayService.hideLoading().then(() => this.navigationService.navigateTo('/error', true, { state: pageParams }));
          },
        });
    });
  }

  public onOpenETA(): void {
    void this.analyticsService.trackEvent({ name: this.analyticsConfig.eventName.catererDetailsPageOpenETA });
    this.isETAOpen = true;
  }

  public onOpenDeliveryInfo(): void {
    void this.analyticsService.trackEvent({ name: this.analyticsConfig.eventName.catererDetailsPageOpenDeliveryInfo });
    this.isVendorDeliveryInfoOpen = true;
  }

  public onOpenMinOrderInfo(): void {
    void this.analyticsService.trackEvent({ name: this.analyticsConfig.eventName.catererDetailsPageOpenMinOrderInfo });
    this.isVendorMinOrderInfoOpen = true;
  }

  public onOpenPolicyInfo(): void {
    void this.analyticsService.trackEvent({ name: this.analyticsConfig.eventName.catererDetailsPageOpenPolicyInfo });
    this.isVendorPolicyOpen = true;
  }

  public onOpenGroupsSideMenu(): void {
    this.isVendorGroupsOpen = true;
  }

  public onScroll(event: Event): void {
    if (!event || !this.showVendorDetails) {
      return;
    }

    this.handleScrollEvent(event as CustomScrollEvent);
  }

  public onNavigateBack(): void {
    void this.navigationService.navigateBack();
  }

  public onMenuItemAvailabilityRequested({ menuItem, groupId }: { menuItem: VendorMenuItem; groupId: number }): void {
    if (!this.selectedAddress) {
      return;
    }

    const params: ItemAvailabilityDateTimePickerModalPageParams = {
      vendorId: menuItem.vendorId,
      menuItemId: menuItem.menuItemId,
      selectedAddress: this.selectedAddress,
      selectedDateTime: this.selectedDateTime,
      isOrderForNowSelected: this.isOrderForNowSelected,
      isOrderForNowAvailable: this.settingsService.isOrderForNowAvailableInServiceType(this.selectedServiceTypes),
    };

    void this.modalService
      .showModal<ItemAvailabilityDateTimePickerModalPageResult>({
        id: ItemAvailabilityDateTimePickerModalPageIdentifier,
        component: ItemAvailabilityDateTimePickerModalPage,
        componentProps: { params },
      })
      .then((availabilityPickerResult) => {
        if (availabilityPickerResult && availabilityPickerResult.hasChangedData) {
          // this.menuItemId = menuItem.menuItemId;
          // this.navigationService.updateDateTimeFromUrl(this.activatedRoute, result.selectedDateTime, result.isOrderForNowSelected);

          void this.overlayService.showLoading().then(() => {
            this.vendorsService
              .getVendorDetailsById({
                serviceTypes: this.selectedServiceTypes,
                vendorId: this.vendorId,
                areaId: this.selectedAreaId,
                dateTime: availabilityPickerResult.selectedDateTime,
                orderForNow: availabilityPickerResult.isOrderForNowSelected,
              })
              .pipe(takeUntil(this.unsubscribe$))
              .subscribe({
                next: (vendorDetails) => {
                  void this.overlayService.hideLoading();

                  const itemDetailsParams: MenuItemDetailsModalParams = {
                    itemId: menuItem.menuItemId,
                    vendorDetails,
                    showAvailabilityMessage: true,
                    selectedServiceType: this.selectedServiceTypes,
                    selectedAddress: this.selectedAddress,
                    selectedDateTime: availabilityPickerResult.selectedDateTime,
                    isOrderForNowSelected: availabilityPickerResult.isOrderForNowSelected,
                    sourceId: this.sourceId,
                    sourceType: this.sourceType,
                    shouldTrackCleverTapEvents: true,
                    incrementQuantity: false,
                    decrementQuantity: false,
                    initializeFromRecentlyOrderedItems: groupId === LocalMenuGroup.RecentlyOrderedItems,
                  };

                  void this.modalService
                    .showModal<MenuItemDetailsModalResult>({
                      component: MenuItemDetailsModalComponent,
                      id: MenuItemDetailsModalPageIdentifier,
                      setAutoHeightOnBrowser: true,
                      componentProps: { itemDetailsParams },
                    })
                    .then((result) => {
                      this.updateStatusBarColorBasedOnHeaderStatus();

                      if (result && result.addedToCart && (result.selectedDateTime || result.isOrderForNowSelected)) {
                        const hasChangedDateTime = result.selectedDateTime !== this.selectedDateTime;
                        const hasChangedOrderForNow = !!result.isOrderForNowSelected !== !!this.isOrderForNowSelected;

                        if (hasChangedDateTime || hasChangedOrderForNow) {
                          this.navigationService.updateDateTimeFromUrl(
                            this.activatedRoute,
                            result.selectedDateTime,
                            result.isOrderForNowSelected,
                          );
                        }
                      }
                    });
                },
                error: (error: HttpErrorResponse) => {
                  const errorMessage = this.validationService.getErrorMessage(error);
                  this.loggerService.info({
                    component: 'VendorDetailsPage',
                    message: "couldn't get vendor details based on availability",
                    details: { error, messageShownToUser: errorMessage },
                  });

                  this.overlayService.showToast({ type: 'error', message: errorMessage, showCloseButton: true });
                },
              });
          });
        }
      });
  }

  public onMenuItemSelected({ menuItem, groupId }: { menuItem: VendorMenuItem; groupId: number }): void {
    this.showMenuItemModal(menuItem.menuItemId, {
      shouldInitializeFromRecentlyOrderedItems: groupId === LocalMenuGroup.RecentlyOrderedItems,
    });
  }

  public onMenuItemDecremented({ menuItem, groupId }: { menuItem: VendorMenuItem; groupId: number }): void {
    this.showMenuItemModal(menuItem.menuItemId, {
      shouldDecrement: true,
      shouldInitializeFromRecentlyOrderedItems: groupId === LocalMenuGroup.RecentlyOrderedItems,
    });
  }

  public onMenuItemIncremented({ menuItem, groupId }: { menuItem: VendorMenuItem; groupId: number }): void {
    this.showMenuItemModal(menuItem.menuItemId, {
      shouldIncrement: true,
      shouldInitializeFromRecentlyOrderedItems: groupId === LocalMenuGroup.RecentlyOrderedItems,
    });
  }

  public onViewOrder(): void {
    this.trackViewOrder();
    this.deepLinkService.openCartPage();
  }

  public onShare(): void {
    if (!this.showVendorDetails) {
      return;
    }

    this.trackShareEvent();

    void this.referralProgramService.maybeShowReferralProgramModal().then((shouldShare) => {
      if (shouldShare) {
        this.universalLinksService
          .shareVendorDetails({
            vendorId: this.vendorId,
            vendorName: this.name,
            vendorDescription: this.description,
            vendorImageUrl: this.imageUrl,
            vendorServiceTypes: this.vendorServiceTypes,
          })
          .catch((error: HttpErrorResponse) => {
            const errorMessage = this.translateService.instant('ERROR_MESSAGE.SHARE') as string;
            this.loggerService.info({
              component: 'VendorDetailsPage',
              message: "couldn't share caterer",
              details: { error, messageShownToUser: errorMessage },
            });

            this.overlayService.showToast({ type: 'error', message: errorMessage });
          });
      }
    });
  }

  public onMenuGroupsRendered(groups: Array<HTMLElement>): void {
    if (groups?.length) {
      this.groupsElements = groups;
      this.updateLastGroupMarginBottom();
      this.initializeGroupsIntersectionObserver();

      const needsToScrollToMenuGroup = this.tabs.findIndex((tab) => tab.serviceType === this.selectedServiceTypes) > 0;
      const loadingImagesDelay = needsToScrollToMenuGroup ? FINISH_LOADING_PAGE_DELAY_IN_MS + SCROLL_TO_GROUP_DURATION_IN_MS : 100;

      void this.platformService.wait(loadingImagesDelay).then(() => {
        if (this.vendorMenuGroupsElements?.nativeElement) {
          const observerId = 'VendorDetailsPage-MenuGroupsLazyLoadingImages';

          this.lazyLoadingImagesObserversIds.push(observerId);
          this.lazyLoadingImagesService.initializeObserver({
            observerId,
            verticalScroll: {
              scrollElement: this.scrollElement,
              imagesContainerElement: this.vendorMenuGroupsElements.nativeElement,
              lazyLoadingOffsetInPixels: 200,
            },
          });
        }
      });
    }
  }

  public onGroceryCategoriesRendered(
    categories: Array<{ categoryId: number; scrollElement: HTMLElement; nativeElement: HTMLElement }>,
  ): void {
    if (categories?.length) {
      this.groupsElements = categories.map((category) => category.nativeElement);
      this.updateLastGroupMarginBottom();
      this.initializeGroupsIntersectionObserver();

      void this.platformService.wait(100).then(() => {
        categories.forEach((category) => {
          const observerId = `VendorDetailsPage-GroceryCategoriesLazyLoadingImages-${category.categoryId}`;

          this.lazyLoadingImagesObserversIds.push(observerId);
          this.lazyLoadingImagesService.initializeObserver({
            observerId,
            horizontalScroll: {
              parentScrollElement: this.scrollElement,
              horizontalScrollElement: category.scrollElement,
            },
          });
        });
      });
    }
  }

  public onFooterChange(): void {
    if (this.isBrowser) {
      return;
    }

    this.updateLastGroupMarginBottom();
  }

  public onChatButtonAnimationStart(): void {
    this.isAnimatingChatButton = true;
  }

  public onChatButtonAnimationEnd(): void {
    this.isAnimatingChatButton = false;
  }

  public onOpenVendorChat(): void {
    if (!this.chatPhoneNumber || !this.vendorDetails) {
      return;
    }

    void this.overlayService.showLoading().then(() => {
      void this.universalLinksService
        .createVendorChatUniversalLink({
          vendorId: this.vendorDetails.vendorId,
          vendorName: this.vendorDetails.name,
          vendorDescription: this.vendorDetails.description,
          vendorImageUrl: this.vendorDetails.imageUrl,
        })
        .then(({ chatId, link }) => {
          void this.overlayService.hideLoading();

          this.vendorsService.updateLatestVendorChatEvent({ chatId, vendorId: this.vendorId });

          void this.analyticsService.trackEvent({
            name: this.analyticsConfig.eventName.vendorDetailsPageChatRequested,
            data: {
              [this.analyticsConfig.propertyName.chatId]: chatId,
              [this.analyticsConfig.propertyName.catererId]: this.vendorId,
            },
          });

          const message = this.translateService.instant('VENDOR_CHAT_MESSAGE', {
            vendorName: encodeURIComponent(this.vendorDetails.name),
            link: encodeURIComponent(link),
          }) as string;

          this.contactService.startChatWithBilbaytUsingPhoneNumber(this.chatPhoneNumber, message);
        });
    });
  }

  public onViewAllItems(): void {
    this.trackViewAllItems();

    this.initialMenuItemTagsIds = [];
    this.reloadVendorDetails();
  }

  private initializeStickyTabsIntersectionObserver(): void {
    if (this.stickyTabsObserver) {
      this.stickyTabsObserver.disconnect();
    }

    if (!this.groupsTabsSentinelElement) {
      return;
    }

    const topInset = this.isBrowser ? 0 : this.cordovaHeaderHeight;
    const options = { rootMargin: `-${topInset}px 0px 0px 0px`, threshold: 0 };

    this.stickyTabsObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        this.ngZone.run(() => (this.isTabBarFixedAtTop = !entry.isIntersecting));
      });
    }, options);

    this.stickyTabsObserver.observe(this.groupsTabsSentinelElement.nativeElement);
  }

  private initializeGroupsIntersectionObserver(): void {
    if (this.activeGroupObserver) {
      this.activeGroupObserver.disconnect();
    }

    const intersectingAreaHeight = 100;
    const topInset = this.isBrowser ? this.tabbarHeight : this.getCordovaHeaderMaxHeight();
    const bottomInset = this.platformService.height() - topInset - intersectingAreaHeight;
    const options = { rootMargin: `-${topInset}px 0px -${bottomInset}px 0px`, threshold: 0 };

    let prevYPosition = 0;
    let direction: 'up' | 'down' = 'up';
    const groupsStatus = this.groupsElements.map(() => false);

    this.activeGroupObserver = new IntersectionObserver((entries) => {
      direction = this.scrollElement.scrollTop > prevYPosition ? 'down' : 'up';
      prevYPosition = this.scrollElement.scrollTop;

      entries.forEach((entry) => {
        const index = this.groupsElements.findIndex((section) => section === entry.target);
        groupsStatus[index] = !!entry.isIntersecting;
      });

      // More than one group can be intersecting at the same time, so we need
      // to decide which one to show as selected based on the scrolling direction
      const targetIndex = direction === 'down' ? groupsStatus.lastIndexOf(true) : groupsStatus.indexOf(true);

      if (this.shouldUpdateActiveGroupOnScroll && targetIndex >= 0) {
        this.updateActiveTab$.next(targetIndex);
      }
    }, options);

    this.groupsElements.forEach((element) => {
      this.activeGroupObserver.observe(element);
    });
  }

  private handleUrlChanges(queryParams: ParamMap): void {
    const areaId = this.navigationService.getAreaIdFromParamMap(queryParams);
    const { dateTime, orderForNow } = this.navigationService.getDateTimeAndOrderForNowFromParamMap(queryParams);

    const addressHasChanged = areaId !== this.selectedAddress?.area.areaId;
    const maybeHandleAreaChangedFromUrl = addressHasChanged ? this.updateLocalStateBasedOnAreaIdChangedFromUrl(areaId) : Promise.resolve();

    void maybeHandleAreaChangedFromUrl.then(() => {
      const orderForNowHasChanged = !!orderForNow !== !!this.isOrderForNowSelected;
      const dateTimeHasChanged = this.dateTimeService.hasChangedDateOrTime(this.selectedDateTime, dateTime);

      if (dateTimeHasChanged || orderForNowHasChanged) {
        this.updateLocalStateBasedOnDateTimeChangeFromUrl(dateTime, orderForNow);
      }

      if (addressHasChanged || dateTimeHasChanged || orderForNowHasChanged) {
        this.reloadVendorDetails();
        return;
      }
    });
  }

  private updateLocalStateBasedOnAreaIdChangedFromUrl(areaIdFromUrl: number): Promise<void> {
    const addressFromSearchFilters = this.vendorsService.getAddressSearchFilters();

    if (!areaIdFromUrl) {
      this.selectedAddress = undefined;
      return Promise.resolve();
    }

    if (addressFromSearchFilters && addressFromSearchFilters.area.areaId === areaIdFromUrl) {
      this.selectedAddress = addressFromSearchFilters;
      return Promise.resolve();
    }

    return Promise.all([
      toPromise(this.areasService.getAreaById(areaIdFromUrl)),
      this.accountService.isLoggedIn() ? toPromise(this.accountService.getUserSavedAddresses(true)) : Promise.resolve([] as Array<Address>),
    ]).then(([area, savedAddresses]) => {
      if (savedAddresses && savedAddresses.length) {
        const existingAddress = savedAddresses.find((address) => address.area.areaId === areaIdFromUrl);

        if (existingAddress) {
          this.selectedAddress = existingAddress;
          return;
        }
      }

      if (area) {
        this.selectedAddress = this.addressService.createNewAddressFromArea(area);
        return;
      }
    });
  }

  private updateLocalStateBasedOnDateTimeChangeFromUrl(dateTime: string, orderForNow: boolean): void {
    this.selectedDateTime = dateTime;
    this.isOrderForNowSelected = orderForNow;
  }

  private validateVendorServiceTypes(serviceTypesFromUrl: number, vendorDefaultServiceTypes: number): number {
    serviceTypesFromUrl =
      serviceTypesFromUrl && this.settingsService.isServiceTypeValid(serviceTypesFromUrl) ? serviceTypesFromUrl : undefined;

    if (!vendorDefaultServiceTypes) {
      return serviceTypesFromUrl;
    }

    if (this.settingsService.isServiceTypeSingle(vendorDefaultServiceTypes)) {
      return vendorDefaultServiceTypes;
    }

    const individualServiceTypes = serviceTypesFromUrl
      ? this.settingsService.getIntersectingServiceTypes(serviceTypesFromUrl, vendorDefaultServiceTypes)
      : this.settingsService.getAllServiceTypesIncludedInServiceTypeCombination(vendorDefaultServiceTypes);

    // We need to check the vendors's default service type but carefully, because it
    // can be a combination of service types that is not valid for the UI — for example
    // 34 which means groceries AND gathering

    const traditionalServiceTypes = individualServiceTypes
      .filter(
        (serviceType) =>
          !this.settingsService.isServiceTypeBasedOnCategories(serviceType) && !this.settingsService.isQuickAddToCartSupported(serviceType),
      )
      .reduce((t, value) => t + value, 0);

    const categoriesServiceTypes = individualServiceTypes
      .filter((serviceType) => this.settingsService.isServiceTypeBasedOnCategories(serviceType))
      .reduce((t, value) => t + value, 0);

    const quickAddToCartServiceTypes = individualServiceTypes
      .filter((serviceType) => this.settingsService.isQuickAddToCartSupported(serviceType))
      .reduce((t, value) => t + value, 0);

    if (this.platformService.isBrowser && !serviceTypesFromUrl) {
      if (traditionalServiceTypes && categoriesServiceTypes) {
        this.alternativeServiceTypeAvailableDetails = {
          serviceType: categoriesServiceTypes,
          message: this.translateService.instant('VENDOR_DETAILS_PAGE.ALTERNATIVE_SERVICE_TYPE_MESSAGE', {
            serviceTypesNames: this.settingsService.getServiceTypesNames(categoriesServiceTypes).toLowerCase(),
          }) as string,
        };
      } else if ((traditionalServiceTypes && quickAddToCartServiceTypes) || (categoriesServiceTypes && quickAddToCartServiceTypes)) {
        this.alternativeServiceTypeAvailableDetails = {
          serviceType: quickAddToCartServiceTypes,
          message: this.translateService.instant('VENDOR_DETAILS_PAGE.ALTERNATIVE_SERVICE_TYPE_MESSAGE', {
            serviceTypesNames: this.settingsService.getServiceTypesNames(quickAddToCartServiceTypes).toLowerCase(),
          }) as string,
        };
      }
    }

    return traditionalServiceTypes || categoriesServiceTypes || quickAddToCartServiceTypes || undefined;
  }

  private initializeScrollElementAndCordovaElementsSizes(): Promise<void> {
    return this.content.getScrollElement().then((scrollElement) => {
      this.scrollElement = scrollElement;

      this.domCtrl.write(() => {
        // This is important so that the content scrolls on top of the header image
        // that have a z-index of 1
        this.scrollElement.style.zIndex = '2';
      });

      if (!this.isBrowser) {
        this.initializeGestureListener();

        this.domCtrl.read(() => {
          // For some reason the height sometimes is not a round number which
          // causes some issues when scrolling to a specific position based on
          // the height of the cordovaHeader
          this.cordovaHeaderHeight = Math.floor(this.cordovaHeader.nativeElement.getBoundingClientRect().height);

          this.domCtrl.write(() => {
            this.contentMarginTop = this.getCordovaHeaderCurrentHeight() + SPACE_BETWEEN_HEADER_AND_CONTENT;
            this.contentMinHeight = this.platformService.height() - this.contentMarginTop;
            this.headerImageMinHeight = this.contentMarginTop + HEADER_IMAGE_OVERFLOW;
          });
        });
      }
    });
  }

  private initializeGestureListener(): void {
    if (this.isCordovaTablet) {
      return;
    }

    this.scrollElement.addEventListener('touchstart', this.handleTouchStartEvent, false);
    this.scrollElement.addEventListener('touchmove', this.handleTouchMoveEvent, false);
    this.scrollElement.addEventListener('touchend', this.handleTouchEndEvent, false);
    this.scrollElement.addEventListener('touchcancel', this.handleTouchEndEvent, false);
  }

  private handleTouchStartEvent = () => {
    this.currentTouchEventsCount++;
  };

  private handleTouchMoveEvent = () => {
    const scrollTop = this.scrollElement.scrollTop;
    const targetImageHeight = this.headerImageMinHeight + Math.abs(scrollTop);

    if (
      scrollTop < 0 &&
      !this.errorWhenSettingHeaderImage &&
      !this.isUpdatingHeaderImageAutomatically &&
      targetImageHeight > this.headerImageInitialHeight
    ) {
      this.headerImageCurrentHeight = targetImageHeight;

      this.domCtrl.write(() => {
        this.headerImageElement.style.height = `${this.headerImageCurrentHeight}px`;
      });
    }
  };

  private handleTouchEndEvent = () => {
    this.currentTouchEventsCount--;

    if (this.currentTouchEventsCount === 0 && this.headerImageCurrentHeight !== this.headerImageInitialHeight) {
      this.isUpdatingHeaderImageAutomatically = true;
      this.headerImageCurrentHeight = this.headerImageInitialHeight;

      this.domCtrl.write(() => {
        this.headerImageElement.style.transition = `height ${HEADER_IMAGE_HEIGHT_TRANSITION_IN_MS}ms ease-out`;
        this.headerImageElement.style.height = `${this.headerImageInitialHeight}px`;
      });

      void this.platformService.wait(HEADER_IMAGE_HEIGHT_TRANSITION_IN_MS + 50).then(() => {
        this.domCtrl.write(() => {
          this.headerImageElement.style.transition = 'none';
        });

        this.isUpdatingHeaderImageAutomatically = false;
      });
    }
  };

  private handleScrollEvent(event: CustomEvent<ScrollEventDetail>): void {
    const scrollingUp = event.detail.currentY < this.currentY;
    this.currentY = event.detail.currentY;

    this.domCtrl.read(() => {
      let showCordovaHeaderBackground = false;
      let hideCordovaHeaderBackground = false;

      const headerHeight = this.isBrowser ? 0 : this.cordovaHeaderHeight;
      const canAnimateChatButton = !!this.chatPhoneNumber && !this.isAnimatingChatButton;
      const expandChatButton = canAnimateChatButton && scrollingUp && !this.showChatButtonExpanded;
      const collapseChatButton = canAnimateChatButton && !scrollingUp && this.showChatButtonExpanded;

      // We hide the vendor details when refreshing the page so it may happen that
      // showVendorDetails was true when the scroll handler was called but now since
      // this is an async function related to the DOM it could become false
      // https://bilbayt.atlassian.net/browse/CA-2854
      if (this.showVendorDetails) {
        const vendorNameTop = this.vendorName ? this.vendorName.nativeElement.getBoundingClientRect().top : 0;

        // Since the scroll event is executed outside of Angular
        // we need to detect changes only when we REALLY need to
        showCordovaHeaderBackground = !this.showCordovaHeaderBackground && vendorNameTop <= headerHeight - 24;
        hideCordovaHeaderBackground = this.showCordovaHeaderBackground && vendorNameTop > headerHeight - 24;
      }

      if (expandChatButton || collapseChatButton || showCordovaHeaderBackground || hideCordovaHeaderBackground) {
        this.ngZone.run(() => {
          if (showCordovaHeaderBackground || hideCordovaHeaderBackground) {
            this.showCordovaHeaderBackground = showCordovaHeaderBackground;
          }

          if (expandChatButton || collapseChatButton) {
            this.showChatButtonExpanded = expandChatButton;
          }
        });
      }

      if (showCordovaHeaderBackground || hideCordovaHeaderBackground) {
        this.updateStatusBarColorBasedOnHeaderStatus();
      }
    });
  }

  private maybeShowChatExplainer(): Promise<void> {
    if (!this.chatPhoneNumber || !this.appExplainersService.shouldShowVendorChatExplainer()) {
      return Promise.resolve();
    }

    this.appExplainersService.markVendorChatExplainerAsAlreadyShown();

    return this.modalService.showSheet<void>({
      id: VendorChatExplainerModalIdentifier,
      component: VendorChatExplainerModalPage,
      breakpoints: undefined,
      initialBreakpoint: undefined,
      animated: false,
      backdropDismiss: false,
      cssClass: 'auto-height',
      componentProps: { vendorName: this.name },
    });
  }

  private updateLastGroupMarginBottom(): void {
    if (!this.windowService.isWindowDefined || !this.groupsElements?.length) {
      return;
    }

    try {
      this.domCtrl.read(() => {
        const groupsBottomSpacingElement = this.windowService.window.document.getElementById('groupsBottomSpacing');

        if (!groupsBottomSpacingElement) {
          return;
        }

        const topArea = this.isBrowser
          ? this.tabbarHeight || 0
          : (this.cordovaHeaderHeight || 0) + (this.isFilteringItems ? this.filteredItemsSectionHeight : 0) + (this.tabbarHeight || 0);

        const lastGroupElement = this.groupsElements[this.groupsElements.length - 1];
        const lastGroupElementHeight = lastGroupElement.offsetHeight || 0;

        // This is the margin bottom required to make the last group to use the entire height of the page
        const menuGroupsBottomSpace = Math.max(this.platformService.height() - lastGroupElementHeight - topArea, 32);

        let marginToBeAdded = 0;

        if (this.footer) {
          const footerElement = this.footer ? this.footer.nativeElement : null;
          const footerElementHeight = footerElement ? footerElement.offsetHeight + 16 : 0;

          // The footer is visible so we need to add enough margin to
          // a) make the last group to use the entire height of the view
          // b) make the content not to be hidden behind the footer
          marginToBeAdded = Math.max(menuGroupsBottomSpace, footerElementHeight);
        } else {
          // The footer is not visible so we just need to add enough margin
          // so that the last group uses the entire height of the view
          marginToBeAdded = menuGroupsBottomSpace;
        }

        this.domCtrl.write(() => {
          groupsBottomSpacingElement.style.height = `${marginToBeAdded}px`;
        });
      });
    } catch (error) {}
  }

  private scrollHorizontallyToSelectedTab(tabIndex: number, retryCount: number = 0): void {
    // Avoid endless lopp
    if (retryCount > 2) {
      return;
    }

    const groupsTabsElement = this.groupsTabsElement?.nativeElement?.querySelectorAll('.tab-wrapper');

    if (groupsTabsElement?.length) {
      void this.scrollService.scrollHorizontallyToSelectedTab(groupsTabsElement[tabIndex] as HTMLElement, 300);
    } else {
      // tabs are not rendered yet so we should try again later
      void this.platformService.wait(300).then(() => this.scrollHorizontallyToSelectedTab(tabIndex, retryCount ? retryCount + 1 : 0));
    }
  }

  private initializeHeaderImagePlaceholder(): void {
    if (this.isBrowser || this.isCordovaTablet) {
      return;
    }

    if (this.headerImageElement) {
      try {
        const platformWidth = this.platformService.width();
        const marginTop = (this.safeAreaInsetsService.getSafeAreaInsetTop() ?? 0) + 40;

        this.domCtrl.write(() => {
          this.headerImageElement.style.transform = 'translate3d(0, 0, 0)';
          this.headerImageElement.style.transition = `opacity ${HEADER_IMAGE_OPACITY_TRANSITION_IN_MS}ms ease-out`;
          this.headerImageElement.style.background = `transparent url("/assets/img/logo.svg") 0 ${marginTop}px/${platformWidth}px 72px no-repeat fixed`;
        });
      } catch (e) {}
    }
  }

  private initializeHeaderImage(): void {
    if (this.isBrowser) {
      return;
    }

    if (!this.headerImageElement) {
      this.errorWhenSettingHeaderImage = true;
      return;
    }

    void this.platformService.preloadImage(this.imageUrl).then(({ loaded, width, height }) => {
      if (!loaded) {
        this.errorWhenSettingHeaderImage = true;
        return;
      }

      // The app won't stretch the image and instead it will only adjust the
      // width so that the image keeps its aspect ratio. Because of that, as the
      // user scrolls down we'll need to calculate when to expand the image and
      // when to just show the rest of the image without expanding it
      // https://bilbayt.atlassian.net/browse/CA-2131

      this.headerImageIntrinsicWidth = width;
      this.headerImageIntrinsicHeight = height;
      this.headerImageInitialWidth = this.platformService.width();
      this.headerImageInitialHeight = this.headerImageCurrentHeight = Math.max(
        this.headerImageMinHeight,
        this.calculateHeaderImageHeightBasedOnWidth(this.headerImageInitialWidth),
      );

      try {
        this.domCtrl.write(() => (this.headerImageElement.style.opacity = '0'));

        void this.platformService.wait(HEADER_IMAGE_OPACITY_TRANSITION_IN_MS).then(() => {
          this.domCtrl.write(() => {
            this.headerImageElement.style.opacity = '1';
            this.headerImageElement.style.willChange = 'height';
            this.headerImageElement.style.height = `${this.headerImageInitialHeight}px`;
            this.headerImageElement.style.background = `transparent url("${this.imageUrl}") center/cover no-repeat scroll`;
          });
        });
      } catch (e) {
        this.errorWhenSettingHeaderImage = true;
      }
    });
  }

  private calculateHeaderImageHeightBasedOnWidth(width: number): number {
    return Math.floor((width * this.headerImageIntrinsicHeight) / this.headerImageIntrinsicWidth);
  }

  private maybeShowVendorAnnouncementPopup(): Promise<void> {
    if (!this.vendorDetails?.announcements?.length) {
      return Promise.resolve();
    }

    const popup = this.vendorDetails.announcements.find(
      (announcement) => !announcement.type || announcement.type === AnnouncementType.Popup,
    );

    if (!popup) {
      return Promise.resolve();
    }

    return this.modalService.showAlert({
      title: popup.title ?? undefined,
      message: popup.message ?? undefined,
      buttons: [{ text: this.translateService.instant('OKAY') as string }],
    });
  }

  private initializeCommonProperties(vendorDetails: VendorDetails | GroceryVendorDetails) {
    this.name = vendorDetails.name;
    this.names = vendorDetails.nameJson ? (JSON.parse(vendorDetails.nameJson) as MultiLanguageValue) : null;
    this.description = vendorDetails.description;
    this.imageUrl = vendorDetails.imageUrl;
    this.rating = vendorDetails.rating;
    this.policy = vendorDetails.policy;
    this.minEta = vendorDetails.minEta?.title && vendorDetails.minEta?.description ? vendorDetails.minEta : null;
    this.vendorServiceTypes = vendorDetails.serviceTypes;

    this.title.setTitle(`${this.name} | ${this.translateService.instant('PAGES.BILBAYT_SUFFIX')}`);

    this.sameDeliveryAcrossServiceTypes = vendorDetails.serviceTypeRequirements.every(
      (requirement) => requirement.minimumDeliveryCharge === vendorDetails.serviceTypeRequirements[0].minimumDeliveryCharge,
    );

    this.sameMinOrderValueAcrossServiceTypes = vendorDetails.serviceTypeRequirements.every(
      (requirement) => requirement.minimumOrderAmount === vendorDetails.serviceTypeRequirements[0].minimumOrderAmount,
    );

    const allServiceTypeDetails = this.settingsService.getServiceTypeDetails();

    this.deliveryInfo = [];

    vendorDetails.serviceTypeRequirements.forEach((serviceTypeRequirements) => {
      this.deliveryInfo.push({
        serviceType: serviceTypeRequirements.serviceType,
        serviceTypeName: allServiceTypeDetails.find(
          (serviceTypeDetails) => serviceTypeDetails.serviceType === serviceTypeRequirements.serviceType,
        ).name,
        minOrderValue: serviceTypeRequirements.minimumOrderAmount,
        minimumDeliveryCharge: serviceTypeRequirements.minimumDeliveryCharge,
      });

      // We're only interested in the MIN delivery charge across all service types
      if (
        this.minimumDeliveryCharge === undefined ||
        this.minimumDeliveryCharge === null ||
        serviceTypeRequirements.minimumDeliveryCharge < this.minimumDeliveryCharge
      ) {
        this.minimumDeliveryCharge = serviceTypeRequirements.minimumDeliveryCharge;
      }

      if (
        this.minOrderValue === undefined ||
        this.minOrderValue === null ||
        serviceTypeRequirements.minimumOrderAmount < this.minOrderValue
      ) {
        this.minOrderValue = serviceTypeRequirements.minimumOrderAmount;
      }
    });
  }

  private trackVendorViewedCleverTapEvent(): void {
    this.analyticsService.trackVendorViewedCleverTapEvent({
      id: this.vendorId,
      nameEn: this.names ? Helpers.getLocalizedMultiLanguageValue(this.names, 'en') : null,
      nameAr: this.names ? Helpers.getLocalizedMultiLanguageValue(this.names, 'ar') : null,
    });
  }

  private trackPageView(): void {
    void this.analyticsService.trackView({
      name: this.analyticsConfig.pageName.catererDetailsPage,
      data: {
        [this.analyticsConfig.propertyName.catererId]: this.vendorId,
        [this.analyticsConfig.propertyName.dateTime]: this.selectedDateTime ?? null,
        [this.analyticsConfig.propertyName.bilbaytNow]: this.isOrderForNowSelected ?? false,
        [this.analyticsConfig.propertyName.selectedTagsId]: this.initialMenuItemTagsIds?.length ? this.initialMenuItemTagsIds : undefined,
      },
      includeAreaDateTime: true,
    });
  }

  private trackViewOrder(): void {
    void this.analyticsService.trackEvent({ name: this.analyticsConfig.eventName.catererDetailsPageTapViewOrder });
  }

  private trackViewAllItems(): void {
    void this.analyticsService.trackEvent({
      name: this.analyticsConfig.eventName.catererDetailsPageViewAllItems,
      data: {
        [this.analyticsConfig.propertyName.catererId]: this.vendorId,
        [this.analyticsConfig.propertyName.selectedTagsId]: this.initialMenuItemTagsIds?.length ? this.initialMenuItemTagsIds : undefined,
      },
    });
  }

  private loadVendorDetailsAndInitializePage({ isReloading } = { isReloading: false }): Promise<void> {
    return new Promise((resolve) => {
      const getGroceryVendorDetails = forkJoin([
        this.vendorsService.getGroceryVendorDetails(this.vendorId, this.selectedAreaId),
        this.vendorsService.getGroceryVendorCategoryItemsPreview(this.vendorId),
      ]).pipe(tap(([vendorDetails, vendorCategoryDetails]) => this.initializeGroceryVendorDetails(vendorDetails, vendorCategoryDetails)));

      const getMenuVendorDetails = this.vendorsService
        .getVendorDetailsById({
          serviceTypes: this.selectedServiceTypes,
          vendorId: this.vendorId,
          areaId: this.selectedAreaId,
          dateTime: this.selectedDateTime,
          orderForNow: this.isOrderForNowSelected,
        })
        .pipe(tap((vendorDetails) => this.initializeMenuVendorDetails(vendorDetails)));

      const getAddressBasedOnAreaId = this.selectedAreaId
        ? this.getAddressBasedOnAreaId().pipe(
            tap((address) => (this.selectedAddress = address)),
            catchError(() => of(undefined)),
          )
        : of(null);

      void this.maybeScrollTop().then(() => {
        from(this.isSelectedServiceTypeBasedOnCategories ? getGroceryVendorDetails : getMenuVendorDetails)
          .pipe(
            switchMap(() => getAddressBasedOnAreaId),
            takeUntil(this.unsubscribe$),
          )
          .subscribe({
            next: () => {
              if (!isReloading) {
                this.initializeHeaderImage();
              }

              this.showVendorDetails = true;

              // Give some time to Angular to finish updating
              // the view before marking the page as "loaded"
              void this.platformService.wait(FINISH_LOADING_PAGE_DELAY_IN_MS).then(() => {
                this.initializeStickyTabsIntersectionObserver();

                const maybeShowChatExplainer = !isReloading ? this.maybeShowChatExplainer() : Promise.resolve();

                void maybeShowChatExplainer.then(() => {
                  if (this.isSelectedServiceTypeBasedOnCategories) {
                    this.activeTabIndex = 0;
                  } else {
                    // We should use the selected service type from the previous page
                    // to select the first menu group of that service type (if possible)
                    const initialTabFromServiceTypeIndex = this.tabs.findIndex((tab) => tab.serviceType === this.selectedServiceTypes);

                    if (!isReloading && initialTabFromServiceTypeIndex > 0) {
                      this.onChangeTab(initialTabFromServiceTypeIndex);
                    } else {
                      this.activeTabIndex = 0;
                    }
                  }

                  this.finishedLoadingPage = true;
                  resolve();

                  // It's better to add a delay because when opening the vendor details page
                  // the app will load all the visible images and also scroll to the selected
                  // item group which could cause some lag
                  void this.platformService.wait(600).then(() => {
                    const maybeShowVendorAnnouncement = this.maybeShowVendorAnnouncementPopup();

                    if (this.menuItemId) {
                      void maybeShowVendorAnnouncement.then(() => this.getMenuItemAndOpenDetails());
                      return;
                    }
                  });
                });
              });
            },
            error: (error: HttpErrorResponse) => {
              const errorMessage = this.validationService.getErrorMessage(error);
              this.loggerService.info({
                component: 'VendorDetailsPage',
                message: "couldn't get vendor details",
                details: { error, messageShownToUser: errorMessage },
              });

              this.finishedLoadingPage = true;

              void this.navigationService.navigateBack().then(() => {
                this.overlayService.showToast({ type: 'error', message: errorMessage, showCloseButton: true });
              });
            },
          });
      });
    });
  }

  private initializeMenuVendorDetails(vendorDetails: VendorDetails): void {
    if (this.initialMenuItemTagsIds?.length && vendorDetails.tags?.length) {
      // Tags sent in the query string param could be manually changed
      // and not even exist in the vendor so we must verify them first
      const selectedTags = this.initialMenuItemTagsIds
        .map((tagId) => vendorDetails.tags.find((tag) => tag.tagId === tagId) || undefined)
        .filter((tag) => !!tag);

      if (selectedTags.length) {
        const updatedMenuGroups = this.vendorsService.filterVendorMenuGroupsBasedOnSelectedTags(vendorDetails.menuGroups, selectedTags);

        if (updatedMenuGroups?.length) {
          vendorDetails.menuGroups = updatedMenuGroups;

          this.isFilteringItems = true;
          this.selectedTagsNames = this.vendorsService.stringifyTags(
            selectedTags.map((tag) => ({ ...tag, name: `<span>${tag.name}</span>`, selected: true })),
            {
              orText: ` ${this.translateService.instant('OR')} `,
              andText: ` ${this.translateService.instant('AND')} `,
            },
          );
        } else {
          // If no items match the selected tag, we should ignore the tags
          // and show all the items
          this.initialMenuItemTagsIds = undefined;
          this.isFilteringItems = false;
          this.selectedTagsNames = undefined;
        }
      }
    } else {
      this.isFilteringItems = false;
      this.selectedTagsNames = undefined;
    }

    this.initializeCommonProperties(vendorDetails);
    this.updateItemsNoticePeriodWarning(vendorDetails);

    this.vendorDetails = vendorDetails;
    this.badges = vendorDetails.badges;
    this.openingTime = this.vendorDetails.serviceStartTime;
    this.closingTime = this.vendorDetails.serviceEndTime;
    this.chatPhoneNumber = !this.platformService.isBrowser ? vendorDetails.chatPhoneNumber || undefined : undefined;

    this.tabs = [];
    this.groupsSideMenuDetails = [];

    const locallyCreatedMenuGroups = this.vendorsService.getLocallyCreatedMenuGroups(this.vendorDetails);

    if (locallyCreatedMenuGroups?.length) {
      this.vendorDetails.menuGroups = [...locallyCreatedMenuGroups, ...this.vendorDetails.menuGroups];
    }

    let groupsSideMenuDetailsCount = 0;

    for (const serviceTypeGroup of this.vendorDetails.menuGroups) {
      const serviceTypeName = this.settingsService.getServiceTypeName(serviceTypeGroup.serviceType);
      const groups: Array<{ name: string; tabIndex: number; itemsCount: number }> = [];

      for (const group of serviceTypeGroup.groups) {
        this.tabs.push({
          serviceTypeName,
          serviceType: serviceTypeGroup.serviceType,
          name: group.name,
        });

        group.serviceTypeName = serviceTypeName;

        const groupName = group.name.includes(`${serviceTypeName} -`) ? group.name.replace(`${serviceTypeName} -`, '').trim() : group.name;

        groups.push({
          name: groupName,
          tabIndex: groupsSideMenuDetailsCount++,
          itemsCount: group.menuItems.length,
        });
      }

      if (groups?.length) {
        this.groupsSideMenuDetails.push({
          serviceTypeName,
          groups,
        });
      }
    }

    this.groupsSideMenuDetailsCount = groupsSideMenuDetailsCount;

    if (this.vendorDetails.reOrderOrders?.length) {
      this.previousOrders = this.vendorDetails.reOrderOrders;
      this.allPreviousOrdersHaveOnlyOneItem = !this.previousOrders.some((o) => o.description);
    } else {
      this.previousOrders = [];
      this.allPreviousOrdersHaveOnlyOneItem = false;
    }
  }

  private initializeGroceryVendorDetails(vendorDetails: GroceryVendorDetails, vendorCategoryDetails: GroceryItemsWithCategories): void {
    this.initializeCommonProperties(vendorDetails);

    this.groceryVendorDetails = vendorDetails;
    this.categoryDetails = vendorCategoryDetails;

    this.tabs = [];
    this.groupsSideMenuDetails = [];

    const serviceTypeName = this.settingsService.getServiceTypeName(this.settingsService.getServiceTypeBasedOnCategories());
    const groups: Array<{ name: string; tabIndex: number; itemsCount: number }> = [];

    this.categoryDetails.categories.forEach((category, index) => {
      this.tabs.push({
        // Show the item's count for grocery categories as the service type is the same for all groups
        serviceTypeName: category.totalItemsCount
          ? (this.translateService.instant('QUANTITY_ITEMS', { quantity: category.totalItemsCount }) as string)
          : undefined,
        serviceType: vendorDetails.serviceTypes,
        name: category.name,
      });

      groups.push({
        name: category.name,
        tabIndex: index,
        itemsCount: category.totalItemsCount,
      });
    });

    if (groups?.length) {
      this.groupsSideMenuDetails.push({ serviceTypeName, groups });
    }

    this.groupsSideMenuDetailsCount = this.categoryDetails.categories?.length ?? 0;
  }

  private getMenuItemAndOpenDetails(): void {
    const itemId = this.menuItemId;
    this.menuItemId = null;

    if (this.isSelectedServiceTypeBasedOnCategories) {
      this.showGroceryItemModal(itemId);
    } else {
      this.showMenuItemModal(itemId);
    }
  }

  private showMenuItemModal(
    itemId: number,
    options?: {
      shouldDecrement?: boolean;
      shouldIncrement?: boolean;
      shouldInitializeFromRecentlyOrderedItems?: boolean;
    },
  ): void {
    const itemDetailsParams: MenuItemDetailsModalParams = {
      itemId,
      vendorDetails: this.vendorDetails,
      selectedServiceType: this.selectedServiceTypes,
      selectedAddress: this.selectedAddress,
      selectedDateTime: this.selectedDateTime,
      isOrderForNowSelected: this.isOrderForNowSelected,
      sourceId: this.sourceId,
      sourceType: this.sourceType,
      shouldTrackCleverTapEvents: true,
      incrementQuantity: options?.shouldIncrement || false,
      decrementQuantity: options?.shouldDecrement || false,
      initializeFromRecentlyOrderedItems: options?.shouldInitializeFromRecentlyOrderedItems || false,
    };

    void this.modalService
      .showModal<MenuItemDetailsModalResult>({
        component: MenuItemDetailsModalComponent,
        id: MenuItemDetailsModalPageIdentifier,
        setAutoHeightOnBrowser: true,
        componentProps: { itemDetailsParams },
      })
      .then((result) => {
        this.updateStatusBarColorBasedOnHeaderStatus();

        if (result && result.addedToCart && (result.selectedDateTime || result.isOrderForNowSelected)) {
          const hasChangedDateTime = result.selectedDateTime !== this.selectedDateTime;
          const hasChangedOrderForNow = !!result.isOrderForNowSelected !== !!this.isOrderForNowSelected;

          if (hasChangedDateTime || hasChangedOrderForNow) {
            this.navigationService.updateDateTimeFromUrl(this.activatedRoute, result.selectedDateTime, result.isOrderForNowSelected);
          }
        }
      });
  }

  private showGroceryItemModal(itemId: number): void {
    void this.overlayService.showLoading().then(() => {
      this.vendorsService
        .getGroceryMenuItemDetails(itemId)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe({
          next: (itemDetails) => {
            const itemDetailsParams: GroceryItemDetailsModalParams = {
              vendorDetails: this.groceryVendorDetails,
              itemDetails,
              selectedAddress: this.selectedAddress,
              shouldTrackCleverTapEvents: true,
            };

            void this.modalService.showCard({
              component: GroceryItemDetailsModalComponent,
              id: GroceryItemDetailsModalPageIdentifier,
              setAutoHeightOnBrowser: true,
              presentingElement: this.elementRef.nativeElement,
              componentProps: { itemDetailsParams },
            });

            void this.overlayService.hideLoading();
          },
          error: (error: HttpErrorResponse) => {
            void this.overlayService.hideLoading().then(() => {
              const errorMessage = this.validationService.getErrorMessage(error);
              this.loggerService.info({
                component: 'VendorDetailsPage',
                message: "couldn't get item details",
                details: { error, messageShownToUser: errorMessage },
              });

              this.overlayService.showToast({ type: 'error', showCloseButton: true, message: errorMessage });
            });
          },
        });
    });
  }

  private maybeHandleAppBeingInBackgroundForTooLong(): boolean {
    const minutesInBackground = this.backgroundService.getMinutesSpentInBackground();

    if (this.isOrderForNowSelected) {
      const shouldUpdateResults =
        minutesInBackground >= MIN_TIME_TO_REFRESH_RESULTS_FOR_NOW_IN_MINUTES &&
        minutesInBackground <= MAX_TIME_TO_REFRESH_RESULTS_FOR_NOW_IN_MINUTES;

      if (shouldUpdateResults) {
        this.reloadVendorDetails();
        return false;
      }

      const shouldRedirect = minutesInBackground > MAX_TIME_TO_REFRESH_RESULTS_FOR_NOW_IN_MINUTES;

      if (shouldRedirect) {
        void this.overlayService
          .showLoading()
          .then(() => void this.navigationService.navigateBack().then(() => void this.overlayService.hideLoading()));
        return false;
      }
    } else {
      if (this.selectedDateTime && this.dateTimeService.isBeforeCurrentLocalTime(this.selectedDateTime)) {
        this.selectedDateTime = null;
        this.isOrderForNowSelected = null;

        void this.overlayService
          .showLoading()
          .then(() => void this.navigationService.navigateBack().then(() => void this.overlayService.hideLoading()));

        return false;
      }

      const shouldUpdate = minutesInBackground >= MIN_TIME_TO_REFRESH_RESULTS_FOR_LATER_IN_MINUTES;

      if (shouldUpdate) {
        this.reloadVendorDetails();
        return false;
      }
    }

    return true;
  }

  private updateItemsNoticePeriodWarning(vendorDetails: VendorDetails): void {
    for (const serviceTypeGroup of vendorDetails?.menuGroups) {
      for (const group of serviceTypeGroup.groups) {
        for (const item of group.menuItems) {
          // The "noticePeriodWarningMessage" property means different things based on if the user already
          // selected a date/time or not:
          // - If there's no date/time, this property will get its value from "preparationMinutesText" and
          //   means that the ETA for this item is different than the ETA shown in the VendorsPage. This is
          //   calculated from the API
          // - If there's a date/time selected, "preparationMinutesText" will be empty so we need to calculate
          //   this warning based on the notice period only if currentTime + bufferTime + notice > selectedDateTime

          item.noticePeriodWarningMessage = !this.selectedDateTime
            ? item.preparationMinutesText ?? null
            : this.noticePeriodService.getNoticePeriodWarningMessage({
                noticePeriod: item.noticePeriod,
                dateTime: this.selectedDateTime,
              });
        }
      }
    }
  }

  private trackShareEvent(): void {
    const isReferralProgramEnabled = this.settingsService.isReferralProgramFeatureEnabled();

    void this.analyticsService.trackEvent({
      name: this.analyticsConfig.eventName.share,
      data: {
        [this.analyticsConfig.propertyName.catererId]: this.vendorId,
        [this.analyticsConfig.propertyName.referralProgram]: Boolean(isReferralProgramEnabled),
      },
    });

    void this.analyticsService.trackEvent({
      name: this.analyticsConfig.eventName.catererDetailsPageShare,
      data: {
        [this.analyticsConfig.propertyName.catererId]: this.vendorId,
        [this.analyticsConfig.propertyName.referralProgram]: Boolean(isReferralProgramEnabled),
      },
    });
  }

  private trackOpenReviews(): void {
    void this.analyticsService.trackEvent({
      name: this.analyticsConfig.eventName.catererDetailsPageOpenReviews,
      data: {
        [this.analyticsConfig.propertyName.catererId]: this.vendorId,
      },
    });
  }

  private updateStatusBarColorBasedOnHeaderStatus(): void {
    if (this.isBrowser) {
      return;
    }

    if (this.showCordovaHeaderBackground) {
      this.statusBarPlugin.useDarkTextColor();
    } else {
      this.statusBarPlugin.useLightTextColor();
    }
  }

  private getAddressBasedOnAreaId(): Observable<Address> {
    if (!this.selectedAreaId) {
      return of(undefined as Address);
    }

    const selectedAddressFromSearch = this.vendorsService.getAddressSearchFilters();

    if (selectedAddressFromSearch && selectedAddressFromSearch.area.areaId === this.selectedAreaId) {
      return of(selectedAddressFromSearch);
    }

    return this.areasService.getAreaById(this.selectedAreaId).pipe(map((area) => this.addressService.createNewAddressFromArea(area)));
  }

  private maybeScrollTop({ extraDelayInMs } = { extraDelayInMs: 0 }): Promise<void> {
    return this.content
      .getScrollElement()
      .then((scrollElement) => (scrollElement.scrollTop !== 0 ? this.content.scrollToTop(300) : Promise.resolve()))
      .then(() => (extraDelayInMs ? this.platformService.wait(extraDelayInMs) : Promise.resolve()));
  }

  private reloadVendorDetails(): void {
    // IMPORTANT: when scrolling up we update some elements from the UI like
    // the header or the tabs in an async way so we must add a small extra delay
    // to make sure calculations are done with the right content
    this.hideVendorDetails = true;
    void this.maybeScrollTop({ extraDelayInMs: 50 }).then(() => {
      this.showVendorDetails = false;
      this.hideVendorDetails = false;
      void this.loadVendorDetailsAndInitializePage({ isReloading: true });
    });
  }

  private getCordovaHeaderMaxHeight(): number {
    return this.isBrowser
      ? this.tabbarHeight || 0
      : (this.cordovaHeaderHeight || 0) + (this.filteredItemsSectionHeight || 0) + (this.tabbarHeight || 0);
  }

  private getCordovaHeaderCurrentHeight(): number {
    return this.isBrowser
      ? this.tabbarHeight || 0
      : (this.cordovaHeaderHeight || 0) +
          (this.isFilteringItems ? this.filteredItemsSectionHeight : 0) +
          (this.isTabBarFixedAtTop ? this.tabbarHeight : 0);
  }
}
